import * as React from 'react';

import { useQuery } from '@apollo/client';
import AllProductSearchQuery from '@graphql/queries/AllProductSearchQuery';
import { Checkbox, CircularProgress, Grid, useTheme } from '@mui/material';
import Paper from '@mui/material/Paper';
import { handleAzureMigratedImages } from 'containers/Product/helper';
import useMiniGlp from 'hooks/useMiniGlp';
import { get } from 'lodash';
import DataTable, { TableColumn } from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import selectWorkspaceProducerId from 'selectors/workspaceProducerId';
import { formatDate } from 'utils/dateTime';

import styles from './AllProductSearch.module.css';
import { tableCustomStyles } from '../../components/ReactDataTable/tableCustomStyles';
import { PRODUCT } from '../../constants/routePaths';
import formatPath from '../../utils/formatPath';
import MaterialButton from '../MaterialButton';




interface Props {
  query: string;
  first: number;
  hideSearch: () => any;
  onClick?: any;
  selectMultiple?: any;
}

interface TableRow {
  id: number;
  image: string;
  name: string;
  brand: string;
  date: string;
  reviews: number;
  lastReview: string;
  textureReviewsLowerBound: number;
  category: string;
  workspace: string;
  productWorkspaceId: number;
  productWorkspaceName: string;
}

const AllProductSearch: React.FC<Props> = ({
  first,
  hideSearch,
  onClick,
  query,
  selectMultiple,
}) => {
  const node = React.useRef(null);
  const [paginateCount, setpaginateCount] = React.useState(first);
  const theme = useTheme();

  const { t } = useTranslation();
  const producerId = useSelector((state) => selectWorkspaceProducerId(state));

  const [selectedRows, setSelectedRows] = React.useState<Array<TableRow>>([]);

  const { data: miniglp } = useMiniGlp();

  const miniGlpMap = React.useMemo(() => {
    const map = new Map<number, { name: string; workspace: string; reviews: number }>();
    if (miniglp) {
      miniglp.forEach((m) => map.set(m.product_id, { name: m.product, workspace: m.producer, reviews: m.reviews }));
    }
    return map;
  }, [miniglp]);


  const handleClick = (e) => {
    // Clicking outside
    if (!(node && node.current && node.current.contains(e.target))) {
      hideSearch();
    }
  };
  React.useEffect(() => {
    document.addEventListener('mousedown', handleClick, false);

    return () => document.removeEventListener('mousedown', handleClick, false);
  }, []);

  const { data, loading, fetchMore } = useQuery(AllProductSearchQuery, {
    variables: { query, producerId, first },
    onCompleted: () => {
      setpaginateCount(paginateCount + first);
    },
  });
  const onFetchMore = () => {
    fetchMore({
      variables: {
        first: paginateCount,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        setpaginateCount(paginateCount + first);
        return Object.assign({}, prev, {
          productResults: {
            nodes: [
              ...prev.productResults.nodes,
              ...fetchMoreResult.productResults.nodes,
            ],
          },
          ...fetchMoreResult,
        });
      },
    });
  };

  //AIPD-524 modify code to get product data with miniGLP names
  const getProductData = (data): TableRow[] => {
    const products = get(data, 'productResults.nodes', []);
    // --comment "return products.map((product) => ({""
    return products.reduce((acc: TableRow[], product) => {
      const miniGlpEntry = miniGlpMap.get(product.id);
      if (!miniGlpEntry || miniGlpEntry.name.length === 0) {
        return acc;
      }
      acc.push({
        id: product.id,
        image:
          get(product, 'productImages.totalCount') > 0 &&
          handleAzureMigratedImages(product.productImages.nodes[0].url),
        name: miniGlpEntry.name,
        brand: product.brand,
        date: formatDate(product.createdAt, 'PP'),
        reviews: miniGlpEntry.reviews ?? get(product, 'productReviews.totalCount'),
        lastReview:
          get(product, 'productReviews.nodes.length') > 0
            ? formatDate(product.productReviews.nodes[0].createdAt, 'PP')
            : '',
        textureReviewsLowerBound: get(product, 'lastAlphabeticalTextureReviews')
          ?.nodes.map((n) => n?.textureCsv?.length > 0)
          .reduce((a, b) => a + b, 0),
        category: product.category,
        workspace: miniGlpEntry.workspace,
        productWorkspaceId: product.producer.id,
        productWorkspaceName: miniGlpEntry.workspace,
      });
      return acc;
    }, []);
  };

  const columns: TableColumn<TableRow>[] = [
    {
      selector: (row: TableRow) => row.id,
      name: 'Product ID',
      sortable: true,
      omit: true,
    },
    {
      selector: (row: TableRow) => row.id + '_checkbox',
      name: 'Select',
      cell: (row: TableRow) => (
        <Checkbox
          onChange={(event, checked) =>
            setSelectedRows((current) =>
              checked
                ? current.concat(row)
                : current.filter((r) => r.id != row.id),
            )
          }
        ></Checkbox>
      ),
    },
    {
      selector: (row: TableRow) => row.image,
      name: t('general.image'),
      cell: (row: TableRow) =>
        row.image ? (
          <div className={styles.imageContainer}>
            <img
              src={row.image}
              alt={`${row.id}_img`}
              className={styles.image}
            />
          </div>
        ) : (
          <div />
        ),
    },
    {
      selector: (row: TableRow) => row.name,
      name: t('product.productName'),
      cell: (row: TableRow) => {
        if (onClick) {
          return (
            <a
              className={styles.link}
              onClick={() => onClick(row, data.productResults.nodes)}
            >
              {row.name}
            </a>
          );
        } else {
          return (
            <Link
              className={styles.link}
              to={{ pathname: formatPath(PRODUCT, { productId: row.id }) }}
            >
              {row.name}
            </Link>
          );
        }
      },
      sortable: true,
    },
    {
      selector: (row: TableRow) => row.brand,
      name: t('product.productBrand'),
      sortable: true,
    },
    {
      selector: (row: TableRow) => row.date,
      name: t('general.dateCreated'),
      sortable: true,
    },
    {
      selector: (row: TableRow) => row.reviews,
      name: t('reviews.reviews'),
    },
    {
      selector: (row: TableRow) => row.workspace,
      name: 'Workspace',
    },
    {
      selector: (row: TableRow) => row.lastReview,
      name: t('reviews.lastReview'),
    },
    {
      selector: (row: TableRow) => row.category,
      name: 'Category',
      omit: true,
    },
  ];

  if (loading && !query) return <div />;

  return (
    <div ref={node} key={paginateCount}>
      {(loading && query && query.length > 0 && (
        <Grid container justifyContent="start" alignItems="center">
          <CircularProgress color="secondary" />
        </Grid>
      )) || (
          <Paper className={styles.container}>
            <div className={styles.tableContainer}>
              <div>
                <DataTable
                  columns={columns}
                  data={getProductData(data)}
                  customStyles={tableCustomStyles(theme, true)}
                  striped
                />
              </div>
              <div style={{ textAlign: 'center', marginBottom: first }}>
                <MaterialButton
                  variant="outlined"
                  soft
                  teal
                  disabled={selectedRows.length == 0}
                  onClick={() =>
                    selectMultiple(selectedRows, data.productResults.nodes)
                  }
                >
                  Select Products
                </MaterialButton>
                &emsp;
                {data.productResults.totalCount >
                  data.productResults.nodes.length && (
                    <MaterialButton
                      variant="outlined"
                      soft
                      onClick={() => onFetchMore()}
                    >
                      Load More
                    </MaterialButton>
                  )}
              </div>
            </div>
          </Paper>
        )}
    </div>
  );
};

export default AllProductSearch;